import React, { useState } from 'react';
import { XIcon, FileTextIcon, MailIcon, CheckIcon, DownloadIcon } from 'lucide-react';
interface Broker {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'active' | 'pending' | 'inactive';
  defaultRate: number;
  rates: {
    [key: string]: number;
  };
}
interface ContractAgreementModalProps {
  broker: Broker;
  onClose: () => void;
  onSend: (brokerId: string) => void;
}
export const ContractAgreementModal = ({
  broker,
  onClose,
  onSend
}: ContractAgreementModalProps) => {
  const [contractType, setContractType] = useState('standard');
  const [customMessage, setCustomMessage] = useState('');
  const [requireSignature, setRequireSignature] = useState(true);
  const [includeRates, setIncludeRates] = useState(true);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSend(broker.id);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1354">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl" data-id="element-1355">
        <div className="flex items-center justify-between p-4 border-b" data-id="element-1356">
          <h3 className="text-lg font-medium flex items-center gap-2" data-id="element-1357">
            <FileTextIcon size={20} data-id="element-1358" />
            Send Agreement to {broker.company}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1359">
            <XIcon size={20} data-id="element-1360" />
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-1361">
          <div className="p-6" data-id="element-1362">
            <div className="mb-4" data-id="element-1363">
              <label className="block text-sm font-medium mb-2" data-id="element-1364">
                Agreement Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1365">
                <label className={`border rounded-md p-3 cursor-pointer flex flex-col ${contractType === 'standard' ? 'border-blue-500 bg-blue-50' : ''}`} data-id="element-1366">
                  <div className="flex items-center mb-2" data-id="element-1367">
                    <input type="radio" name="contractType" value="standard" checked={contractType === 'standard'} onChange={() => setContractType('standard')} className="mr-2" data-id="element-1368" />
                    <span className="font-medium" data-id="element-1369">Standard Agreement</span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-1370">
                    Basic terms for broker relationships. Includes standard
                    rates and job expectations.
                  </p>
                </label>
                <label className={`border rounded-md p-3 cursor-pointer flex flex-col ${contractType === 'longterm' ? 'border-blue-500 bg-blue-50' : ''}`} data-id="element-1371">
                  <div className="flex items-center mb-2" data-id="element-1372">
                    <input type="radio" name="contractType" value="longterm" checked={contractType === 'longterm'} onChange={() => setContractType('longterm')} className="mr-2" data-id="element-1373" />
                    <span className="font-medium" data-id="element-1374">Long-term Partnership</span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-1375">
                    Extended terms for established brokers with volume
                    commitments and discounted rates.
                  </p>
                </label>
                <label className={`border rounded-md p-3 cursor-pointer flex flex-col ${contractType === 'custom' ? 'border-blue-500 bg-blue-50' : ''}`} data-id="element-1376">
                  <div className="flex items-center mb-2" data-id="element-1377">
                    <input type="radio" name="contractType" value="custom" checked={contractType === 'custom'} onChange={() => setContractType('custom')} className="mr-2" data-id="element-1378" />
                    <span className="font-medium" data-id="element-1379">Custom Agreement</span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-1380">
                    Upload or create a custom agreement tailored to this
                    specific broker relationship.
                  </p>
                </label>
              </div>
            </div>
            <div className="mb-4" data-id="element-1381">
              <div className="flex items-center justify-between mb-2" data-id="element-1382">
                <label className="text-sm font-medium" data-id="element-1383">Agreement Preview</label>
                <button type="button" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1" data-id="element-1384">
                  <DownloadIcon size={16} data-id="element-1385" />
                  Download PDF
                </button>
              </div>
              <div className="border rounded-md p-4 bg-gray-50 h-64 overflow-y-auto" data-id="element-1386">
                <div className="text-center mb-4" data-id="element-1387">
                  <h3 className="text-lg font-bold" data-id="element-1388">BROKER AGREEMENT</h3>
                  <p className="text-sm text-gray-600" data-id="element-1389">
                    Between Contractor and {broker.company}
                  </p>
                </div>
                <p className="mb-2 text-sm" data-id="element-1390">
                  This Broker Agreement (the "Agreement") is entered into by and
                  between [Your Company Name] ("Contractor") and{' '}
                  {broker.company} ("Broker"), effective as of the date of
                  execution.
                </p>
                <h4 className="font-medium mb-1 text-sm" data-id="element-1391">
                  1. SCOPE OF SERVICES
                </h4>
                <p className="mb-2 text-sm" data-id="element-1392">
                  Broker agrees to provide trucking services to Contractor as
                  requested, including but not limited to arranging for the
                  transportation of materials, equipment, and supplies to and
                  from job sites.
                </p>
                <h4 className="font-medium mb-1 text-sm" data-id="element-1393">
                  2. RATES AND PAYMENT
                </h4>
                <p className="mb-2 text-sm" data-id="element-1394">
                  Contractor agrees to pay Broker for services rendered
                  according to the rate schedule attached as Exhibit A. Payment
                  terms are net 30 days from the date of invoice.
                </p>
                <h4 className="font-medium mb-1 text-sm" data-id="element-1395">
                  3. TERM AND TERMINATION
                </h4>
                <p className="mb-2 text-sm" data-id="element-1396">
                  This Agreement shall remain in effect for one (1) year from
                  the effective date and shall automatically renew for
                  successive one-year terms unless terminated by either party
                  with 30 days written notice.
                </p>
                <p className="text-xs text-gray-500 italic mt-4" data-id="element-1397">
                  [This is a preview. The full agreement will be sent to the
                  broker for review and signature.]
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1398">
              <div data-id="element-1399">
                <label className="block text-sm font-medium mb-2" data-id="element-1400">
                  Options
                </label>
                <div className="space-y-2" data-id="element-1401">
                  <label className="flex items-center" data-id="element-1402">
                    <input type="checkbox" checked={requireSignature} onChange={() => setRequireSignature(!requireSignature)} className="mr-2" data-id="element-1403" />
                    <span className="text-sm" data-id="element-1404">
                      Require electronic signature
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-1405">
                    <input type="checkbox" checked={includeRates} onChange={() => setIncludeRates(!includeRates)} className="mr-2" data-id="element-1406" />
                    <span className="text-sm" data-id="element-1407">
                      Include rate schedule as Exhibit A
                    </span>
                  </label>
                </div>
              </div>
              <div data-id="element-1408">
                <label className="block text-sm font-medium mb-2" data-id="element-1409">
                  Custom Message (Optional)
                </label>
                <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md p-2" placeholder="Add a personal message to accompany the agreement..." data-id="element-1410"></textarea>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start gap-3" data-id="element-1411">
              <CheckIcon size={20} className="text-green-500 mt-0.5 flex-shrink-0" data-id="element-1412" />
              <div data-id="element-1413">
                <h4 className="font-medium text-green-800" data-id="element-1414">
                  Agreement Ready to Send
                </h4>
                <p className="text-sm text-green-700 mt-1" data-id="element-1415">
                  This agreement will be sent to {broker.name} at {broker.email}
                  . You'll be notified when they view and sign the document.
                </p>
              </div>
            </div>
          </div>
          <div className="border-t px-6 py-4 flex justify-end gap-3" data-id="element-1416">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1417">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2" data-id="element-1418">
              <MailIcon size={18} data-id="element-1419" />
              Send Agreement
            </button>
          </div>
        </form>
      </div>
    </div>;
};